import React, { ChangeEvent, useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { Divider } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import Button from 'components/common/Button/Button';
import TextInput from 'components/common/TextInput/TextInput';
import InputSelect from 'components/common/InputSelect/InputSelect';
import PhoneNumberInput from 'components/common/PhoneNumberInput/PhoneNumberInput';
import {
  IsMainContactOption,
  UserProfileFormInput,
  UserProfileFormTab,
  UserProfileOpenMode,
} from 'components/users/users.enums';
import useForm from 'hooks/useForm';
import { isValidPhone, validatePercentageValue, validateRequiredField } from 'common/utils/validation.utils';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { validateEmail } from 'components/auth/authValidation.utils';
import {
  IS_MAIN_CONTACT_SELECT_OPTIONS,
  ACCOUNT_STATUS_SELECT_OPTIONS,
  accountStatusesForUpdate,
  ACCOUNT_STATUS_TOOLTIP,
} from 'components/users/users.constants';
import Checkbox from 'components/common/Checkbox/Checkbox';
import TabsPanel from 'components/common/TabsPanel';
import { RootStoreContext } from 'index';
import Spinner from 'components/common/Spinner/Spinner';
import { DoneDarkIcon, ErrorMessageIcon } from 'components/common/Icons/Icons';
import { validateRolesAndPermissionsRequired } from 'components/users/users.utils';
import NumberInput from 'components/common/NumberInput/NumberInput';
import { MainContactPosition } from 'components/intermediaryApplications/intermediaryApplications.enums';
import { UserProfileFormValues } from 'components/users/UsersManagement/UserProfileForm/userProfileForm.types';
import Tooltip from 'components/common/Tooltip/Tooltip';

import styles from './UserProfileForm.module.scss';
import { AccountStatus } from '../../users.enums';

interface UserProfileFormProps {
  userId?: number;
  openMode: UserProfileOpenMode;
  onClose: () => void;
}

function UserProfileForm({ userId, openMode, onClose }: UserProfileFormProps) {
  const { usersStore, rolesStore, permissionsStore, authStore } = useContext(RootStoreContext);
  const {
    createUser,
    user,
    isUserProfileLoading,
    getUserById,
    updateUser,
    resendInvitation,
    isSendingInvitationLoading,
  } = usersStore;
  const { roles, getAllRoles } = rolesStore;
  const { permissions, getAllPermissions } = permissionsStore;
  const { account } = authStore;
  const ownerRole = roles.find((role) => role.name === MainContactPosition.Owner);
  const [currentTab, setCurrentTab] = useState<UserProfileFormTab>(UserProfileFormTab.Roles);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const [isInitialDataLoading, setIsInitialDataLoading] = useState<boolean>(true);
  const [wasInvitationSent, setWasInvitationSent] = useState<boolean>(false);

  const defaultValues: UserProfileFormValues = {
    [UserProfileFormInput.FirstName]: '',
    [UserProfileFormInput.LastName]: '',
    [UserProfileFormInput.Phone]: '',
    [UserProfileFormInput.Email]: '',
    [UserProfileFormInput.Position]: '',
    [UserProfileFormInput.IsMainContact]: '',
    [UserProfileFormInput.AccountStatus]: null,
    [UserProfileFormInput.SendInvitation]: false,
    [UserProfileFormInput.Roles]: {},
    [UserProfileFormInput.Permissions]: {},
    [UserProfileFormInput.PercentageOwnedOfCompany]: null,
  };

  const { errors, values, setValue, validateField, handleSubmit, setErrors, resetForm } =
    useForm<UserProfileFormValues>({
      defaultValues: defaultValues,
      handleValidation: (fieldName, value) => {
        switch (fieldName) {
          case UserProfileFormInput.FirstName: {
            return validateRequiredField(value, fieldName);
          }
          case UserProfileFormInput.LastName: {
            return validateRequiredField(value, fieldName);
          }
          case UserProfileFormInput.Phone: {
            return !!value ? isValidPhone(value) : '';
          }
          case UserProfileFormInput.Email: {
            return validateRequiredField(value, fieldName) || validateEmail(value, fieldName);
          }
          case UserProfileFormInput.IsMainContact: {
            return validateRequiredField(value, fieldName);
          }
          case UserProfileFormInput.Roles: {
            return validateRolesAndPermissionsRequired(value, 'User must have at least one role.');
          }
          case UserProfileFormInput.Permissions: {
            return validateRolesAndPermissionsRequired(value, 'User must have at least one permission.');
          }
          case UserProfileFormInput.PercentageOwnedOfCompany: {
            if (values[UserProfileFormInput.Roles][ownerRole!.id]) {
              return validatePercentageValue(value);
            }

            return '';
          }
        }

        return '';
      },
    });

  const loadInitialData = async () => {
    try {
      setIsInitialDataLoading(true);

      if ([UserProfileOpenMode.Edit, UserProfileOpenMode.View].includes(openMode)) {
        await getUserById(userId!);
      }

      await getAllRoles();
      await getAllPermissions();
    } catch (error: any) {
      notify(error.message || ToastMessage.DefaultError, 'error');
    } finally {
      setIsInitialDataLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if ([UserProfileOpenMode.Edit, UserProfileOpenMode.View].includes(openMode) && user) {
      const newDefaultValues: UserProfileFormValues = {
        [UserProfileFormInput.FirstName]: user.firstName,
        [UserProfileFormInput.LastName]: user.lastName,
        [UserProfileFormInput.Phone]: user.phone,
        [UserProfileFormInput.Email]: user.email,
        [UserProfileFormInput.Position]: user.position || '',
        [UserProfileFormInput.IsMainContact]: user.isMainContact ? IsMainContactOption.Yes : IsMainContactOption.No,
        [UserProfileFormInput.SendInvitation]: false,
        [UserProfileFormInput.AccountStatus]: user.accountStatus,
        [UserProfileFormInput.Roles]: Object.assign(
          {},
          ...roles.map((role) => ({ [role.id]: !!user.roleIds.find((roleId) => roleId === role.id) })),
        ),
        [UserProfileFormInput.Permissions]: Object.assign(
          {},
          ...permissions.map((permission) => ({
            [permission.id]: !!user.permissionIds.find((permissionId) => permissionId === permission.id),
          })),
        ),
        [UserProfileFormInput.PercentageOwnedOfCompany]: user.percentageOwnedOfCompany,
      };

      resetForm(newDefaultValues);
    } else if (openMode === UserProfileOpenMode.Create) {
      const newDefaultValues: UserProfileFormValues = {
        ...defaultValues,
        [UserProfileFormInput.Roles]: Object.assign({}, ...roles.map((role) => ({ [role.id]: false }))),
        [UserProfileFormInput.Permissions]: Object.assign(
          {},
          ...permissions.map((permission) => ({
            [permission.id]: false,
          })),
        ),
      };

      resetForm(newDefaultValues);
    }
  }, [openMode, permissions, roles, user]);

  const changeCurrentTab = (newTab: UserProfileFormTab) => {
    setCurrentTab(newTab);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    if (name === UserProfileFormInput.SendInvitation) {
      setValue(name, !values[UserProfileFormInput.SendInvitation]);
    } else {
      setValue(name, value);
    }
  };

  const handleBlur = (event: any): void => {
    const { name } = event.target;

    validateField(name, values[name as keyof UserProfileFormValues]);
  };

  const handleSaveEditedUser = handleSubmit(async () => {
    try {
      setIsSaveLoading(true);

      if (user!.id === account?.id && values[UserProfileFormInput.Email].toLowerCase() !== user!.email) {
        notify(ToastMessage.YouCanChangeYourOwnEmailOnMyProfilePage, 'error');

        return;
      }

      const accountStatus = values[UserProfileFormInput.AccountStatus];
      let roleIds = Object.keys(values[UserProfileFormInput.Roles])
        .filter((roleId) => values[UserProfileFormInput.Roles][+roleId])
        .map(Number);

      if (ownerRole && roleIds.includes(ownerRole.id)) {
        roleIds = [ownerRole.id];
      }

      await updateUser(user!.id, {
        email: values[UserProfileFormInput.Email].toLowerCase(),
        isMainContact: values[UserProfileFormInput.IsMainContact] === IsMainContactOption.Yes,
        position: values[UserProfileFormInput.Position],
        phone: values[UserProfileFormInput.Phone] || null,
        firstName: values[UserProfileFormInput.FirstName],
        lastName: values[UserProfileFormInput.LastName],
        roleIds: roleIds,
        permissionIds: Object.keys(values[UserProfileFormInput.Permissions])
          .filter((permissionId) => values[UserProfileFormInput.Permissions][+permissionId])
          .map(Number),
        percentageOwnedOfCompany: values[UserProfileFormInput.PercentageOwnedOfCompany],
        accountStatus: accountStatus && accountStatusesForUpdate.includes(accountStatus) ? accountStatus : undefined,
      });

      onClose();

      notify(ToastMessage.UserUpdated, 'success');
    } catch (error: any) {
      notify(error.message || ToastMessage.DefaultError, 'error');
    } finally {
      setIsSaveLoading(false);
    }
  });

  const handleSaveInformation = handleSubmit(async () => {
    try {
      setIsSaveLoading(true);

      await createUser({
        email: values[UserProfileFormInput.Email].toLowerCase(),
        isMainContact: values[UserProfileFormInput.IsMainContact] === IsMainContactOption.Yes,
        position: values[UserProfileFormInput.Position],
        phone: values[UserProfileFormInput.Phone] || null,
        firstName: values[UserProfileFormInput.FirstName],
        lastName: values[UserProfileFormInput.LastName],
        isSendInvitation: values[UserProfileFormInput.SendInvitation],
        roleIds: Object.keys(values[UserProfileFormInput.Roles])
          .filter((roleId) => values[UserProfileFormInput.Roles][+roleId])
          .map(Number),
        permissionIds: Object.keys(values[UserProfileFormInput.Permissions])
          .filter((permissionId) => values[UserProfileFormInput.Permissions][+permissionId])
          .map(Number),
        percentageOwnedOfCompany: values[UserProfileFormInput.PercentageOwnedOfCompany],
      });

      onClose();

      notify(ToastMessage.UserCreated, 'success');
    } catch (error: any) {
      notify(error.message || ToastMessage.DefaultError, 'error');
    } finally {
      setIsSaveLoading(false);
    }
  });

  const handleChangePercentageOwnedOfCompany = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(UserProfileFormInput.PercentageOwnedOfCompany, +event.target.value);

    const error = validatePercentageValue(+event.target.value);

    if (error) {
      setErrors({
        [UserProfileFormInput.PercentageOwnedOfCompany]: error,
      });
    }
  };

  const renderRolesAndPermissionsTab = () => {
    const userProfileInputName =
      currentTab === UserProfileFormTab.Roles ? UserProfileFormInput.Roles : UserProfileFormInput.Permissions;

    const isPermissionChecked = (permissionId: number): boolean => {
      const selectedRoles = roles.filter((role) => values[UserProfileFormInput.Roles]?.[role.id]);

      for (const role of selectedRoles) {
        if (role.permissionIds.find((permission) => permission === permissionId)) {
          return true;
        }
      }

      return false;
    };

    const handleRoleCheckboxChange = (roleId: number) => {
      const updatedValues = { ...values[UserProfileFormInput.Roles] };

      updatedValues[roleId] = !updatedValues[roleId];

      const selectedRoles = roles.filter((role) => updatedValues?.[role.id]);
      const unSelectedRoles = roles.filter((role) => !updatedValues?.[role.id]);

      if (unSelectedRoles.length) {
        for (const role of unSelectedRoles) {
          if (role.permissionIds.length) {
            role.permissionIds.forEach((permissionId) => {
              values[UserProfileFormInput.Permissions][permissionId] = false;
            });
          }
        }
      }

      if (selectedRoles.length) {
        for (const role of selectedRoles) {
          if (role.permissionIds.length) {
            role.permissionIds.forEach((permissionId) => {
              values[UserProfileFormInput.Permissions][permissionId] = true;
            });
          }
        }
      }

      setValue(UserProfileFormInput.Roles, updatedValues);
    };

    const handlePermissionCheckboxChange = (permissionId: number) => {
      const updatedValues = { ...values[userProfileInputName] };

      updatedValues[permissionId] = !updatedValues[permissionId];

      setValue(userProfileInputName, updatedValues);
    };

    return (
      <div className={styles.rolesContainer}>
        {Object.keys(values[userProfileInputName])
          .map(Number)
          .map((checkboxName) => {
            return (
              <div key={checkboxName}>
                <Divider />
                <div className={styles.checkboxContainer}>
                  {currentTab === UserProfileFormTab.Roles && (
                    <Checkbox
                      label={roles.find((role) => role.id === checkboxName)?.name}
                      onChange={() => {
                        handleRoleCheckboxChange(checkboxName);
                      }}
                      name={checkboxName.toString()}
                      errorMessage={''}
                      isChecked={values[userProfileInputName][checkboxName]}
                      disabled={openMode === UserProfileOpenMode.View}
                    />
                  )}
                  {currentTab === UserProfileFormTab.Permission && (
                    <Checkbox
                      label={permissions.find((permission) => permission.id === checkboxName)?.name}
                      onChange={() => {
                        handlePermissionCheckboxChange(checkboxName);
                      }}
                      name={checkboxName.toString()}
                      errorMessage={''}
                      isChecked={values[userProfileInputName][checkboxName]}
                      disabled={isPermissionChecked(checkboxName) || openMode === UserProfileOpenMode.View}
                    />
                  )}
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const handleResendInvitation = async () => {
    try {
      await resendInvitation(user!.id);
      setWasInvitationSent(true);
    } catch (error: any) {
      notify(error.message, 'error');
    }
  };

  const getSendInvitationLabelText = () => {
    if (wasInvitationSent) {
      return 'Invitation to use this portal sent';
    }

    if (user!.isInvitationSend) {
      return 'Resend Invitation To Use This Portal';
    }

    return 'Send Invitation To Use This Portal';
  };

  const checkMainContactSelectIsDisabled = () => {
    if (openMode === UserProfileOpenMode.View) {
      return true;
    }

    if (openMode === UserProfileOpenMode.Edit && user!.isMainContact) {
      return true;
    }

    return false;
  };

  if (isUserProfileLoading || isInitialDataLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <TextInput
        useRequiredSymbol={openMode === UserProfileOpenMode.Create}
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={UserProfileFormInput.FirstName}
        name={UserProfileFormInput.FirstName}
        value={values[UserProfileFormInput.FirstName]}
        errorMessage={errors[UserProfileFormInput.FirstName]}
        onChange={handleChange}
        disabled={openMode === UserProfileOpenMode.View}
      />
      <TextInput
        useRequiredSymbol={openMode !== UserProfileOpenMode.View}
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={UserProfileFormInput.LastName}
        name={UserProfileFormInput.LastName}
        value={values[UserProfileFormInput.LastName]}
        errorMessage={errors[UserProfileFormInput.LastName]}
        onChange={handleChange}
        disabled={openMode === UserProfileOpenMode.View}
      />
      <PhoneNumberInput
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={UserProfileFormInput.Phone}
        onChange={(value: any) => setValue(UserProfileFormInput.Phone, value)}
        data-label={UserProfileFormInput.Phone}
        value={values[UserProfileFormInput.Phone]}
        disabled={openMode === UserProfileOpenMode.View}
        errorMessage={errors[UserProfileFormInput.Phone]}
      />
      <TextInput
        className={styles.input}
        useRequiredSymbol={openMode !== UserProfileOpenMode.View}
        containerClassName={styles.inputContainer}
        labelTitle={UserProfileFormInput.Email}
        name={UserProfileFormInput.Email}
        value={values[UserProfileFormInput.Email]}
        errorMessage={errors[UserProfileFormInput.Email]}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={openMode === UserProfileOpenMode.View}
      />
      <TextInput
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={UserProfileFormInput.Position}
        name={UserProfileFormInput.Position}
        value={values[UserProfileFormInput.Position]}
        errorMessage={errors[UserProfileFormInput.Position]}
        onChange={handleChange}
        disabled={openMode === UserProfileOpenMode.View}
      />
      <InputSelect
        items={IS_MAIN_CONTACT_SELECT_OPTIONS}
        disabled={checkMainContactSelectIsDisabled()}
        currentValue={values[UserProfileFormInput.IsMainContact]}
        onChange={(value) => setValue(UserProfileFormInput.IsMainContact, value)}
        label={UserProfileFormInput.IsMainContact}
        errorMessage={errors[UserProfileFormInput.IsMainContact]}
        useRequiredSymbol={openMode !== UserProfileOpenMode.View}
      />
      {values[UserProfileFormInput.Roles][ownerRole!.id] && (
        <NumberInput
          label={UserProfileFormInput.PercentageOwnedOfCompany}
          name={UserProfileFormInput.PercentageOwnedOfCompany}
          value={String(values[UserProfileFormInput.PercentageOwnedOfCompany])}
          errorMessage={errors[UserProfileFormInput.PercentageOwnedOfCompany]}
          inputMode="numeric"
          onChange={handleChangePercentageOwnedOfCompany}
          disabled={openMode === UserProfileOpenMode.View}
        />
      )}
      {openMode !== UserProfileOpenMode.Create && (
        <Tooltip title={ACCOUNT_STATUS_TOOLTIP} placement="top">
          <div>
            <InputSelect
              items={ACCOUNT_STATUS_SELECT_OPTIONS}
              disabled={
                openMode === UserProfileOpenMode.View ||
                (values[UserProfileFormInput.AccountStatus] !== AccountStatus.Active &&
                  values[UserProfileFormInput.AccountStatus] !== AccountStatus.Disabled) ||
                user!.id === account?.id
              }
              currentValue={values[UserProfileFormInput.AccountStatus] || undefined}
              onChange={(value) => setValue(UserProfileFormInput.AccountStatus, value)}
              label={UserProfileFormInput.AccountStatus}
              errorMessage={errors[UserProfileFormInput.AccountStatus]}
            />
          </div>
        </Tooltip>
      )}
      {openMode === UserProfileOpenMode.Edit && user!.accountStatus !== AccountStatus.Active && (
        <Button
          isLoading={isSendingInvitationLoading}
          onClick={handleResendInvitation}
          variant="white"
          className={styles.resendButton}
          disabled={wasInvitationSent}
        >
          {wasInvitationSent ? <DoneDarkIcon /> : <AutorenewIcon />}
          {getSendInvitationLabelText()}
        </Button>
      )}
      {openMode === UserProfileOpenMode.Create && (
        <Checkbox
          label={UserProfileFormInput.SendInvitation}
          onChange={handleChange}
          name={UserProfileFormInput.SendInvitation}
          errorMessage={errors[UserProfileFormInput.SendInvitation]}
        />
      )}
      <TabsPanel
        defaultTabId={UserProfileFormTab.Roles}
        tabs={[
          {
            id: UserProfileFormTab.Roles,
            name: UserProfileFormTab.Roles,
            onClick: () => changeCurrentTab(UserProfileFormTab.Roles),
          },
          {
            id: UserProfileFormTab.Permission,
            name: UserProfileFormTab.Permission,
            onClick: () => changeCurrentTab(UserProfileFormTab.Permission),
          },
        ]}
      />
      <Divider />
      {renderRolesAndPermissionsTab()}
      <div className={styles.errorsContainer}>
        {!!errors[UserProfileFormInput.Roles] && (
          <div className={styles.errorMessageContainer}>
            <ErrorMessageIcon />
            <div className={styles.errorMessage}>{errors[UserProfileFormInput.Roles]}</div>
          </div>
        )}
        {!!errors[UserProfileFormInput.Permissions] && (
          <div className={styles.errorMessageContainer}>
            <ErrorMessageIcon />
            <div className={styles.errorMessage}>{errors[UserProfileFormInput.Permissions]}</div>
          </div>
        )}
      </div>
      {openMode === UserProfileOpenMode.Create && (
        <Button onClick={handleSaveInformation} className={styles.saveButton} isLoading={isSaveLoading}>
          Create User
        </Button>
      )}
      {openMode === UserProfileOpenMode.View && (
        <Button onClick={onClose} className={styles.closeButton}>
          Close
        </Button>
      )}
      {openMode === UserProfileOpenMode.Edit && (
        <Button onClick={handleSaveEditedUser} className={styles.saveButton} isLoading={isSaveLoading}>
          Save Changes
        </Button>
      )}
    </>
  );
}

export default observer(UserProfileForm);
